<script lang="ts">
	import { get } from 'svelte/store';
	//Formatting
	import { format } from 'svelte-i18n';
	//Design system components
	import { Text, Token } from '@Shelly/shelly-design-system';
	//Helpers
	import { getWebHookEvents } from 'src/helpers/actions/webhooks/core/webhook_helpers';
	import { capFirstChar } from 'src/helpers/utilities';
	import { getCameraMotionZones } from 'src/components/Scenes/helpers/device_conditions/custom_cases/camera/camera-device-helpers';
	import {
		getAvailableCameraWebhookModes,
		getAvailableCameraWebhookStates,
		getCameraWebhookInitialSelection,
		getCameraWebhookModes,
		getCameraWebhookZoneComponent,
	} from 'src/helpers/actions/webhooks/conditions/camera_webhook_conditions_helpers';
	//Stores
	import { devices_settings } from 'src/stores/device-runtime/status';
	//Types
	import type { Device } from 'src/types/Device';
	import type {
		CameraWebhookMode,
		CameraWebhookState,
		WebHookEventOption,
		WebHooksParams,
		WebHookSupportedList,
	} from 'src/types/requests/settings/webhooks';

	export let device: Device;
	export let actionProps: WebHooksParams;
	export let supportedActionsList: WebHookSupportedList;
	export let eventsIndex = -1;
	export let componentIndex = 0;
	export let eventsOptions: WebHookEventOption[] = [];
	export let components: WebHooksParams['components'] = [];

	const initialSelection = getCameraWebhookInitialSelection(actionProps);
	const motionZones = getCameraMotionZones(device.id, get(devices_settings));
	const cameraComponent = components?.find((item) => item.type === 'camera')?.component;

	const selectComponent = (component?: string): boolean => {
		const index = components?.findIndex((item) => item.component === component) ?? -1;
		if (index < 0) return false;

		componentIndex = index;
		actionProps = { ...actionProps, ...components[index] };
		const events = getWebHookEvents(actionProps, supportedActionsList, device);
		eventsOptions = events.options;
		eventsIndex = eventsOptions.length === 1 ? 0 : events.index;
		return true;
	};

	const clearEvent = () => {
		eventsIndex = -1;
		actionProps = { ...actionProps, event: '' };
	};

	const selectMode = (mode: string) => {
		selectedMode = mode;
		selectedZone = '';
		selectedState = '';

		const modeConfig = availableModes.find((item) => item.id === mode);
		if (modeConfig && !modeConfig.usesZones) {
			selectComponent(modeConfig.component);
		}
		clearEvent();
	};

	const selectZone = (zone: string) => {
		selectedZone = zone;
		selectedState = '';
		selectComponent(getCameraWebhookZoneComponent(zone));
		clearEvent();
	};

	const selectState = (state: CameraWebhookState) => {
		selectedState = state.id;

		if (!selectedModeConfig || !selectComponent(selectedModeConfig.component)) {
			clearEvent();
			return;
		}

		const eventOption = eventsOptions.find((option) => option.event === state.event);
		eventsIndex = eventOption?.value ?? -1;
		actionProps.event = eventOption?.event ?? '';
	};

	let selectedMode = initialSelection.selectedMode;
	let selectedZone = initialSelection.selectedZone;
	let selectedState = initialSelection.selectedState;
	let modes: CameraWebhookMode[] = [];
	let selectedModeConfig: CameraWebhookMode | undefined;
	let stateOptions: CameraWebhookState[] = [];
	let availableModes: CameraWebhookMode[] = [];

	$: {
		modes = getCameraWebhookModes($format, cameraComponent, motionZones, selectedZone);
		availableModes = getAvailableCameraWebhookModes(modes, motionZones, supportedActionsList);
		if (!availableModes.some((mode) => mode.id === selectedMode)) {
			selectedMode = availableModes[0]?.id || '';
			selectedState = '';
		}
		selectedModeConfig = availableModes.find((mode) => mode.id === selectedMode);
		stateOptions = getAvailableCameraWebhookStates(selectedModeConfig, supportedActionsList);
	}
</script>

<Text element="h2" class="mb-s">
	{$format('label_select_device_property', { default: 'Select device property' })}
</Text>
<div class="d-flex flex-wrap gap-s">
	{#each availableModes as mode}
		<Token
			id="token-camera-webhook-{mode.tokenId}"
			title={mode.title}
			size="l"
			selected={selectedMode === mode.id}
			on:click={() => selectMode(mode.id)}
		/>
	{/each}
</div>

{#if selectedModeConfig?.usesZones}
	<Text element="h2" class="mb-s mt-m">
		{$format('select_zone', { default: 'Select Zone' })}
	</Text>
	<div class="d-flex flex-wrap gap-s">
		{#each motionZones as zone}
			<Token
				id="token-camera-webhook-zone-{zone.id}"
				title={capFirstChar(zone.title)}
				size="m"
				selected={selectedZone === zone.value}
				on:click={() => selectZone(zone.value)}
			/>
		{/each}
	</div>
{/if}

{#if selectedModeConfig && (!selectedModeConfig.usesZones || selectedZone)}
	<Text element="h2" class="mb-s mt-m">
		{selectedModeConfig.heading}
	</Text>
	<div class="d-flex flex-wrap gap-s">
		{#each stateOptions as state}
			<Token
				id="token-camera-webhook-{selectedModeConfig.tokenId}-{state.id}"
				title={state.title}
				size="m"
				icon={state.icon}
				selected={selectedState === state.id}
				on:click={() => selectState(state)}
			/>
		{/each}
	</div>
{/if}
